import { useMemo, useState } from 'react';
import { Drawer } from 'vaul';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory } from 'react-router-dom';
import { Eye } from 'lucide-react';

import FilterSection from './FilterSection';
import FilterSectionWithOptions from './FilterSectionWithOptions';
import {
  setHeightFilters,
  setSizeType,
  setSizeFilters,
  setTypeFilters,
  setBrandFilters,
  applyFilters,
  selectDraftFilters,
  selectDraftFilterCount,
  resetFilters,
} from './ReviewsFilterDrawer.duck';
import { getAvailableReviewFilterOptions } from '../../ReviewsPage.duck';
import { addInchesToHeight } from '../../../../util/string';
import { CLOTHING_SIZES, SHOE_SIZES } from '../../../../util/size';
import buttonCss from '../../../LandingPage/components/HomepageButtons.module.css';

import css from './ReviewsFilterDrawer.module.css';

const ReviewsFilterDrawer = ({
  draftFilters,
  draftFilterCount,
  availableReviewFilterOptions,
  onHeightChange,
  onSizeTypeChange,
  onSizeChange,
  onTypeChange,
  onBrandChange,
  onApplyFilters,
  onResetFilters,
}) => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);

  const ensureSelectedOption = (options, selectedValue, createFallbackOption) => {
    if (!selectedValue || options.some(option => option.value === selectedValue)) {
      return options;
    }

    return [...options, createFallbackOption(selectedValue)];
  };

  const heightOptions = useMemo(() => {
    const mappedOptions = availableReviewFilterOptions.heights.map(value => ({
      value: addInchesToHeight(value),
      label: addInchesToHeight(value),
    }));

    return ensureSelectedOption(mappedOptions, draftFilters.pub_height, value => ({
      value,
      label: value,
    }));
  }, [availableReviewFilterOptions.heights, draftFilters.pub_height]);

  const typeOptions = useMemo(() => {
    const mappedOptions = availableReviewFilterOptions.categories.map(value => ({
      value,
      label: value.charAt(0).toUpperCase() + value.slice(1),
    }));

    return ensureSelectedOption(mappedOptions, draftFilters.pub_categoryLevel1, value => ({
      value,
      label: value.charAt(0).toUpperCase() + value.slice(1),
    }));
  }, [availableReviewFilterOptions.categories, draftFilters.pub_categoryLevel1]);

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      onApplyFilters();
    }
  };

  const handleShowResults = () => {
    onApplyFilters();
    setIsOpen(false);
  };

  const handleClearAll = () => {
    onResetFilters();
    history.replace('/reviews');
  };

  const currentSizes = useMemo(() => {
    const availableSizes =
      draftFilters.sizeType === 'clothing'
        ? availableReviewFilterOptions.clothingSizes
        : availableReviewFilterOptions.shoeSizes;
    const baseSizes = draftFilters.sizeType === 'clothing' ? CLOTHING_SIZES : SHOE_SIZES;

    return ensureSelectedOption(availableSizes, draftFilters.size, value => {
      return baseSizes.find(option => option.value === value) || { value, label: value };
    });
  }, [
    availableReviewFilterOptions.clothingSizes,
    availableReviewFilterOptions.shoeSizes,
    draftFilters.size,
    draftFilters.sizeType,
  ]);

  const activeBrandValue = useMemo(() => {
    if (draftFilters.pub_brandSlug) {
      return draftFilters.pub_brandSlug;
    }

    if (draftFilters.brandName) {
      const matchingOption = availableReviewFilterOptions.brands.find(
        option => option.queryParam === 'brandName' && option.queryValue === draftFilters.brandName
      );

      return matchingOption?.value || `brand-name:${draftFilters.brandName}`;
    }

    return '';
  }, [availableReviewFilterOptions.brands, draftFilters.brandName, draftFilters.pub_brandSlug]);

  const brandOptions = useMemo(() => {
    return ensureSelectedOption(availableReviewFilterOptions.brands, activeBrandValue, value => ({
      value,
      label: draftFilters.brandName || value,
    }));
  }, [activeBrandValue, availableReviewFilterOptions.brands, draftFilters.brandName]);

  const handleBrandChange = value => {
    const selectedOption = brandOptions.find(option => option.value === value);

    if (!selectedOption) {
      onBrandChange({ pub_brandSlug: '', brandName: '' });
      return;
    }

    if (selectedOption.queryParam === 'brandName') {
      onBrandChange({
        pub_brandSlug: '',
        brandName: selectedOption.queryValue || selectedOption.label,
      });
      return;
    }

    onBrandChange({ pub_brandSlug: selectedOption.queryValue || value, brandName: '' });
  };

  return (
    <div onKeyDown={handleKeyPress}>
      <Drawer.Root open={isOpen} onOpenChange={setIsOpen} direction="right">
        <Drawer.Trigger asChild>
          <button
            className={`${css.triggerButton} ${buttonCss.button} ${buttonCss.buttonSecondary} ${buttonCss.buttonLight}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`${css.triggerIcon} ${buttonCss.icon}`}
            >
              <path d="M10 5H3" />
              <path d="M12 19H3" />
              <path d="M14 3v4" />
              <path d="M16 17v4" />
              <path d="M21 12h-9" />
              <path d="M21 19h-5" />
              <path d="M21 5h-7" />
              <path d="M8 10v4" />
              <path d="M8 12H3" />
            </svg>
            <span>FILTERS</span>
          </button>
        </Drawer.Trigger>

        <Drawer.Portal>
          <Drawer.Overlay className={css.overlay} />
          <Drawer.Content className={css.content}>
            <Drawer.Title className={css.visuallyHidden}>Filter Reviews</Drawer.Title>
            <Drawer.Description className={css.visuallyHidden}></Drawer.Description>
            <div className={css.header}>
              <h1 className={css.title}>Review filters</h1>
              <Drawer.Close asChild>
                <button className={css.closeButton}>✕</button>
              </Drawer.Close>
            </div>

            <div className={css.sectionsContainer}>
              <FilterSection
                title="Height"
                options={heightOptions}
                selectedOptions={draftFilters.pub_height}
                onChange={onHeightChange}
                isScrollable={true}
              />

              <FilterSectionWithOptions
                title="Size"
                sizeType={draftFilters.sizeType}
                options={currentSizes}
                selectedOptions={draftFilters.size}
                onChange={onSizeChange}
                onSizeTypeChange={onSizeTypeChange}
                isScrollable={true}
                isGrid={true}
              />

              <FilterSection
                title="Type"
                options={typeOptions}
                selectedOptions={draftFilters.pub_categoryLevel1}
                onChange={onTypeChange}
                isScrollable={true}
              />

              <FilterSection
                title="Brand"
                options={brandOptions}
                selectedOptions={activeBrandValue}
                onChange={handleBrandChange}
                isScrollable={true}
              />

              {draftFilterCount > 0 && (
                <div className={css.resetFilters} onClick={handleClearAll}>
                  Clear all ({draftFilterCount})
                </div>
              )}
            </div>

            <div className={css.footer}>
              <button
                className={`${css.showResultsButton} ${buttonCss.button} ${buttonCss.buttonPrimary} ${buttonCss.buttonDark}`}
                onClick={handleShowResults}
              >
                <Eye
                  className={`${css.showResultsIcon} ${buttonCss.icon}`}
                  aria-hidden="true"
                  size={18}
                  strokeWidth={2}
                  absoluteStrokeWidth
                />
                <span>SHOW RESULTS</span>
              </button>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    draftFilters: selectDraftFilters(state),
    draftFilterCount: selectDraftFilterCount(state),
    availableReviewFilterOptions: getAvailableReviewFilterOptions(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onHeightChange: payload => dispatch(setHeightFilters(payload)),
  onSizeTypeChange: payload => dispatch(setSizeType(payload)),
  onSizeChange: payload => dispatch(setSizeFilters(payload)),
  onTypeChange: payload => dispatch(setTypeFilters(payload)),
  onBrandChange: payload => dispatch(setBrandFilters(payload)),
  onApplyFilters: () => dispatch(applyFilters()),
  onResetFilters: () => dispatch(resetFilters()),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ReviewsFilterDrawer);
